import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { HubViewportCard } from "@/components/layout/HubViewportCard";
import { Input, Label, TextArea } from "@/components/ui/Input";
import { SortableTableHeader, type SortDirection } from "@/components/ui/SortableTableHeader";
import { PageTitleWithHelp } from "@/components/ui/title-help";
import { useHubToast } from "@/components/ui/HubToast";
import { useHubModuleWrite } from "@/lib/useHubModuleWrite";
import { EM_DASH, cn } from "@/lib/utils";

import { createQualityRca, fetchQualityRcas, type QualityRcaListRow } from "./qualityRcaApi";

const HELP_TEXT =
  "A root cause analysis can be opened from an RMA, an NCR, an event, or a case; none of them open one automatically. Search the list and open a row, or create one here and link records on the RCA page.";

type RcaSortKey = "id" | "status" | "problem" | "rma" | "createdAt";
type StatusFilter = "all" | "open" | "closed";

function formatDate(value: string | null): string {
  if (!value) return EM_DASH;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

function compareRcas(left: QualityRcaListRow, right: QualityRcaListRow, key: RcaSortKey, dir: SortDirection): number {
  const direction = dir === "asc" ? 1 : -1;
  switch (key) {
    case "id":
      return (left.id - right.id) * direction;
    case "status":
      return left.status.localeCompare(right.status) * direction;
    case "problem":
      return (left.problemStatement ?? left.summary ?? "").localeCompare(right.problemStatement ?? right.summary ?? "") * direction;
    case "rma":
      return (left.rmaNumber ?? "").localeCompare(right.rmaNumber ?? "") * direction;
    case "createdAt":
    default:
      return (new Date(left.createdAt).getTime() - new Date(right.createdAt).getTime()) * direction;
  }
}

export function QualityRcasPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showToast } = useHubToast();
  const writesAllowed = useHubModuleWrite("quality");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [sortKey, setSortKey] = useState<RcaSortKey>("createdAt");
  const [sortDir, setSortDir] = useState<SortDirection>("desc");
  const [showCreate, setShowCreate] = useState(false);
  const [problem, setProblem] = useState("");

  const listQuery = useQuery({
    queryKey: ["quality", "rcas", search, statusFilter],
    queryFn: () =>
      fetchQualityRcas({
        search: search.trim() || undefined,
        status: statusFilter === "all" ? undefined : statusFilter,
        limit: 200,
      }),
  });

  const rows = useMemo(() => {
    const loaded = listQuery.data?.rows ?? [];
    return [...loaded].sort((left, right) => compareRcas(left, right, sortKey, sortDir));
  }, [listQuery.data?.rows, sortKey, sortDir]);

  const toggleSort = (key: RcaSortKey) => {
    if (sortKey === key) {
      setSortDir((current) => (current === "asc" ? "desc" : "asc"));
      return;
    }
    setSortKey(key);
    setSortDir(key === "status" || key === "problem" || key === "rma" ? "asc" : "desc");
  };

  const createMutation = useMutation({
    mutationFn: () => createQualityRca({ problemStatement: problem.trim() || null }),
    onSuccess: async (created) => {
      setShowCreate(false);
      setProblem("");
      await queryClient.invalidateQueries({ queryKey: ["quality", "rcas"] });
      showToast({ tone: "success", message: `Created RCA ${created.rca.id}.` });
      void navigate(`/quality/rcas/${created.rca.id}`);
    },
    onError: (error: Error) => showToast({ tone: "error", message: error.message }),
  });

  const filtersActive = search.trim() !== "" || statusFilter !== "all";

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <PageTitleWithHelp hoverHint={HELP_TEXT}>Root cause analyses</PageTitleWithHelp>
        {writesAllowed && !showCreate ? (
          <Button onClick={() => setShowCreate(true)}>New RCA</Button>
        ) : null}
      </header>

      {showCreate ? (
        <Card>
          <CardHeader>
            <CardTitle>New RCA</CardTitle>
          </CardHeader>
          <CardBody className="space-y-3">
            <div>
              <Label htmlFor="rca-list-problem">Problem statement</Label>
              <TextArea
                id="rca-list-problem"
                value={problem}
                rows={3}
                onChange={(event) => setProblem(event.target.value)}
              />
            </div>
            <div className="flex flex-wrap justify-end gap-2">
              <Button variant="secondary" onClick={() => setShowCreate(false)}>
                Cancel
              </Button>
              <Button disabled={createMutation.isPending} onClick={() => createMutation.mutate()}>
                Create RCA
              </Button>
            </div>
          </CardBody>
        </Card>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
        </CardHeader>
        <CardBody className="grid gap-3 md:grid-cols-3">
          <div className="md:col-span-2">
            <Label htmlFor="quality-rca-search">Search RCAs</Label>
            <Input
              id="quality-rca-search"
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Filter by RCA number or problem statement"
            />
          </div>
          <div>
            <Label htmlFor="quality-rca-status">Status</Label>
            <select
              id="quality-rca-status"
              className={cn(
                "mt-1.5 flex h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm",
                "ring-offset-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400",
              )}
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value as StatusFilter)}
            >
              <option value="all">All</option>
              <option value="open">Open</option>
              <option value="closed">Closed</option>
            </select>
          </div>
        </CardBody>
      </Card>

      <HubViewportCard>
        <CardHeader>
          <CardTitle>
            {listQuery.isLoading ? "Loading RCAs…" : `${rows.length} RCA${rows.length === 1 ? "" : "s"}`}
          </CardTitle>
        </CardHeader>
        <CardBody className="min-h-0 overflow-auto p-0">
          {listQuery.isError ? (
            <p className="p-4 text-sm text-red-700">{(listQuery.error as Error).message}</p>
          ) : rows.length === 0 ? (
            <p className="p-4 text-sm text-slate-600">
              {filtersActive ? "No rows match these filters." : "No RCAs yet."}
            </p>
          ) : (
            <table className="min-w-full text-sm">
              <thead className="sticky top-0 z-10 border-b border-slate-200 bg-slate-50/95 text-left text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <SortableTableHeader label="RCA" sortKey="id" activeKey={sortKey} dir={sortDir} onSort={toggleSort} />
                  <SortableTableHeader
                    label="Status"
                    sortKey="status"
                    activeKey={sortKey}
                    dir={sortDir}
                    onSort={toggleSort}
                  />
                  <SortableTableHeader
                    label="Problem"
                    sortKey="problem"
                    activeKey={sortKey}
                    dir={sortDir}
                    onSort={toggleSort}
                  />
                  <SortableTableHeader label="RMA" sortKey="rma" activeKey={sortKey} dir={sortDir} onSort={toggleSort} />
                  <SortableTableHeader
                    label="Opened"
                    sortKey="createdAt"
                    activeKey={sortKey}
                    dir={sortDir}
                    onSort={toggleSort}
                  />
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr
                    key={row.id}
                    className="cursor-pointer border-b border-slate-100 hover:bg-slate-50/80"
                    onClick={() => navigate(`/quality/rcas/${row.id}`)}
                  >
                    <td className="px-2 py-3 tabular-nums">
                      <Link className="text-sky-700 hover:underline" to={`/quality/rcas/${row.id}`}>
                        {row.id}
                      </Link>
                    </td>
                    <td className="px-2 py-3">{row.status === "closed" ? "Closed" : "Open"}</td>
                    <td className="px-2 py-3">{row.problemStatement ?? row.summary ?? EM_DASH}</td>
                    <td className="px-2 py-3">
                      {row.rmaId == null || row.rmaNumber == null ? (
                        EM_DASH
                      ) : (
                        <Link
                          className="text-sky-700 hover:underline"
                          to={`/quality/rmas/${row.rmaId}`}
                          onClick={(event) => event.stopPropagation()}
                        >
                          {row.rmaNumber}
                        </Link>
                      )}
                    </td>
                    <td className="px-2 py-3">{formatDate(row.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardBody>
      </HubViewportCard>
    </div>
  );
}
